import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, CheckCircle, Fire, Barbell, Plant, Clock,
  Sparkle, ForkKnife, Moon, Minus,
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { PLANS, LUNCH_PLAN, DINNER_PLAN, PRICING } from '@/data/plans';
import { ProgressStepper } from '@/components/ProgressStepper';
import { ConfettiBurst } from '@/components/ConfettiBurst';
import CustomOrderModal from '@/components/CustomOrderModal';

const NOVA_LOGO = 'https://customer-assets.emergentagent.com/job_nova-fit-meals/artifacts/8r5uywp4_Nova%20Foods.png';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUB_META = {
  'post-workout': {
    icon: Barbell,
    title: 'Post-Workout Meals',
    desc: 'High-protein meals designed to fuel recovery after every workout.',
    color: '#1A4331',
    tag: '5 meals/week',
  },
  'lunch': {
    icon: ForkKnife,
    title: 'Lunch Subscription',
    desc: 'Balanced, protein-rich meals for your everyday fitness routine.',
    color: '#0F766E',
    tag: '5 meals/week',
  },
  'dinner': {
    icon: Moon,
    title: 'Dinner Subscription',
    desc: 'Light, nutritious and protein-focused dinners for a healthy lifestyle.',
    color: '#4338CA',
    tag: '5 meals/week',
  },
};

const INITIAL_SUBS = {
  'post-workout': { selected: false, plan: null, billing: 'weekly' },
  'lunch': { selected: false, billing: 'weekly' },
  'dinner': { selected: false, billing: 'weekly' },
};

const PlansPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedSubs, setSelectedSubs] = useState(INITIAL_SUBS);
  const [mealChoices, setMealChoices] = useState({ 'post-workout': {}, 'lunch': {}, 'dinner': {} });
  const [formData, setFormData] = useState({
    start_date: '', full_name: '', mobile: '', email: '',
    address_line1: '', address_line2: '', city: '', state: '', zipcode: '',
    fitness_center_id: '', fitness_center_name: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [fitnessCenters, setFitnessCenters] = useState([]);
  const [customOrderOpen, setCustomOrderOpen] = useState(false);
  const [removedCard, setRemovedCard] = useState(null);
  const [flashedOption, setFlashedOption] = useState(null); // { subType, day, opt }

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/fitness-centers`)
      .then(res => setFitnessCenters(res.data || []))
      .catch(() => setFitnessCenters([]));
  }, []);

  // ─── Computed ─────────────────────────────────────────────────────────────
  const activeSubs = Object.entries(selectedSubs)
    .filter(([, v]) => v.selected)
    .map(([k]) => k);

  const pwSub = selectedSubs['post-workout'];
  const canStep1Continue =
    activeSubs.length > 0 &&
    (!pwSub.selected || (pwSub.selected && pwSub.plan !== null));

  const getSubPrice = (type) => {
    const sub = selectedSubs[type];
    if (type === 'post-workout') {
      if (!sub.plan) return PRICING['weight-loss'][sub.billing]; // default for display
      return PRICING[sub.plan][sub.billing];
    }
    return PRICING[type][sub.billing];
  };

  const orderTotal = activeSubs.reduce((sum, t) => sum + getSubPrice(t), 0);

  const getMealsForSub = (type) => {
    if (type === 'post-workout') return PLANS[selectedSubs[type].plan]?.meals || [];
    if (type === 'lunch') return LUNCH_PLAN.meals;
    return DINNER_PLAN.meals;
  };

  const getPlanDataForSub = (type) => {
    if (type === 'post-workout') return PLANS[selectedSubs[type].plan];
    if (type === 'lunch') return LUNCH_PLAN;
    return DINNER_PLAN;
  };

  const canStep2Continue = () =>
    activeSubs.every(type => {
      const meals = getMealsForSub(type);
      return meals.filter(m => m.options).every(m => !!mealChoices[type][m.day]);
    });

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const toggleSub = (type) => {
    const wasSelected = selectedSubs[type].selected;
    setSelectedSubs(prev => ({ ...prev, [type]: { ...prev[type], selected: !prev[type].selected } }));
    if (wasSelected) {
      setRemovedCard(type);
      setTimeout(() => setRemovedCard(null), 650);
    }
  };

  const updateSub = (type, field, value) =>
    setSelectedSubs(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));

  const handleMealChoice = (subType, day, choice) => {
    setMealChoices(prev => ({ ...prev, [subType]: { ...prev[subType], [day]: choice } }));
    setFlashedOption({ subType, day, opt: choice });
    setTimeout(() => setFlashedOption(null), 700);
  };

  const handleStep1Continue = () => {
    if (activeSubs.length === 0) {
      toast.error('Please select at least one subscription');
      return;
    }
    if (pwSub.selected && !pwSub.plan) {
      toast.error('Please select a goal (Weight Loss or Muscle Gain) for Post-Workout');
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStep2Continue = () => {
    if (!canStep2Continue()) {
      toast.error('Please make all meal selections before continuing');
      return;
    }
    // Auto-fill defaults for no-option days
    const updated = {};
    activeSubs.forEach(type => {
      updated[type] = { ...mealChoices[type] };
      getMealsForSub(type).forEach(m => {
        if (!m.options && !updated[type][m.day]) updated[type][m.day] = m.name;
      });
    });
    setMealChoices(updated);
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateForm = () => {
    const required = ['start_date', 'full_name', 'mobile', 'email', 'address_line1', 'city', 'state', 'zipcode', 'fitness_center_id'];
    for (const f of required) {
      if (!formData[f]?.trim()) { toast.error(`Please fill in ${f.replace(/_/g, ' ')}`); return false; }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { toast.error('Please enter a valid email'); return false; }
    if (!/^[0-9+\-\s]{10,15}$/.test(formData.mobile)) { toast.error('Please enter a valid mobile number'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const subscriptionsPayload = activeSubs.map(type => ({
        type,
        ...(type === 'post-workout' ? { plan: selectedSubs[type].plan } : {}),
        billing: selectedSubs[type].billing,
        meal_choices: mealChoices[type] || {},
      }));
      const legacyPlanType = pwSub.selected ? pwSub.plan : activeSubs[0];
      const response = await axios.post(`${BACKEND_URL}/api/subscription/submit`, {
        subscriptions: subscriptionsPayload,
        plan_type: legacyPlanType,
        selected_options: mealChoices['post-workout'] || {},
        ...formData,
      });
      setConfirmation(response.data);
      setStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Subscription request submitted!');
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── JSX ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-stone-100" data-testid="plans-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 font-heading text-2xl font-semibold text-[#1A4331]">
            <img src={NOVA_LOGO} alt="Nova Foods" className="h-10 w-10 rounded-lg object-cover" />
            Nova Foods
          </button>
          {step > 1 && step < 4 && (
            <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 text-stone-600 hover:text-[#1A4331] font-medium" data-testid="back-btn">
              <ArrowLeft size={20} weight="bold" /> Back
            </button>
          )}
        </div>
      </header>

      {/* Progress */}
      {step < 4 && (
        <div className="max-w-3xl mx-auto px-6 pt-8" data-testid="plans-stepper-wrap">
          <div className="bg-white/70 backdrop-blur-xl border border-stone-200/60 rounded-2xl p-4 shadow-sm">
            <ProgressStepper steps={['Plans', 'Meals', 'Details', 'Confirmed']} current={step} />
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">

          {/* ── STEP 1: Select subscriptions ───────────────────────────── */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} data-testid="step-1">
              <div className="text-center mb-12">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4" data-testid="plans-heading">
                  Choose Your Meal Plan
                </h1>
                <p className="text-base leading-relaxed text-stone-600 max-w-2xl mx-auto">
                  Build your meal routine — choose Post-Workout, Lunch, Dinner, or all three in one request.
                </p>
              </div>

              {/* Subscription type cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                {Object.entries(SUB_META).map(([type, meta]) => {
                  const sub = selectedSubs[type];
                  const Icon = meta.icon;
                  const pricing = type === 'post-workout'
                    ? PRICING['weight-loss']
                    : PRICING[type];

                  return (
                    <motion.div
                      key={type}
                      whileHover={{ y: sub.selected ? 0 : -6 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => toggleSub(type)}
                      className={`relative bg-white rounded-3xl cursor-pointer transition-all duration-300 p-7 ${
                        sub.selected
                          ? 'shadow-xl border-2'
                          : 'shadow-sm hover:shadow-lg border border-stone-100'
                      }`}
                      style={sub.selected ? { borderColor: meta.color } : {}}
                      data-testid={`sub-card-${type}`}
                    >
                      {/* Deselect pulse ring */}
                      <AnimatePresence>
                        {removedCard === type && (
                          <motion.span
                            key="pulse"
                            initial={{ opacity: 0.55, scale: 1 }}
                            animate={{ opacity: 0, scale: 1.06 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.55, ease: 'easeOut' }}
                            className="absolute inset-0 rounded-3xl pointer-events-none border-2"
                            style={{ borderColor: meta.color }}
                          />
                        )}
                      </AnimatePresence>

                      {/* Selected badge */}
                      <div
                        className={`absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
                          sub.selected ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
                        }`}
                        style={{ backgroundColor: meta.color }}
                      >
                        <CheckCircle size={18} weight="fill" className="text-white" />
                      </div>

                      {/* Icon */}
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                        style={{ backgroundColor: `${meta.color}18`, color: meta.color }}
                      >
                        <Icon size={28} weight="duotone" />
                      </div>

                      <h3 className="text-xl font-semibold text-[#1C1917] font-heading mb-2">{meta.title}</h3>
                      <p className="text-sm text-stone-600 leading-relaxed mb-3">{meta.desc}</p>
                      <div
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
                        style={{ backgroundColor: `${meta.color}12`, color: meta.color }}
                      >
                        <CheckCircle size={12} weight="fill" />
                        {meta.tag}
                      </div>

                      {/* Expanded options */}
                      <AnimatePresence>
                        {sub.selected && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                            onClick={e => e.stopPropagation()}
                          >
                            <div className="pt-5 mt-5 border-t border-stone-100 space-y-4">

                              {/* Post-Workout: goal selection */}
                              {type === 'post-workout' && (
                                <div>
                                  <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Choose Your Goal</p>
                                  <div className="grid grid-cols-2 gap-2">
                                    {[
                                      { key: 'weight-loss', label: 'Weight Loss', icon: Fire },
                                      { key: 'muscle-gain', label: 'Muscle Gain', icon: Barbell },
                                    ].map(({ key, label, icon: PlanIcon }) => (
                                      <button
                                        key={key}
                                        onClick={() => updateSub('post-workout', 'plan', key)}
                                        className={`flex items-center gap-2 p-3 rounded-xl border text-sm font-medium transition-all ${
                                          sub.plan === key
                                            ? 'border-[#1A4331] bg-[#1A433108] text-[#1A4331]'
                                            : 'border-stone-200 text-stone-600 hover:border-stone-300'
                                        }`}
                                        data-testid={`pw-goal-${key}`}
                                      >
                                        <PlanIcon size={15} weight="duotone" />
                                        {label}
                                      </button>
                                    ))}
                                  </div>
                                  {!sub.plan && (
                                    <p className="text-[11px] text-amber-600 mt-2 font-medium">Select a goal to continue</p>
                                  )}
                                </div>
                              )}

                              {/* Billing cycle */}
                              <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Billing Cycle</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {[
                                    { key: 'weekly', label: 'Weekly', price: pricing.weekly },
                                    { key: 'monthly', label: 'Monthly', price: pricing.monthly, badge: 'BEST VALUE' },
                                  ].map(({ key, label, price, badge }) => (
                                    <button
                                      key={key}
                                      onClick={() => updateSub(type, 'billing', key)}
                                      className={`p-3 rounded-xl text-left transition-all ${
                                        sub.billing === key ? 'border-2' : 'border border-stone-200 hover:border-stone-300'
                                      }`}
                                      style={sub.billing === key ? { borderColor: meta.color, backgroundColor: `${meta.color}08` } : {}}
                                      data-testid={`billing-${type}-${key}`}
                                    >
                                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-0.5">{label}</p>
                                      <p className="text-base font-bold" style={sub.billing === key ? { color: meta.color } : { color: '#1C1917' }}>
                                        ₹{price.toLocaleString()}
                                      </p>
                                      {badge && <p className="text-[10px] text-emerald-600 font-bold mt-0.5">{badge}</p>}
                                    </button>
                                  ))}
                                </div>
                              </div>

                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>

              {/* Order Summary */}
              <AnimatePresence>
                {activeSubs.length > 0 && (
                  <motion.div
                    key="summary"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 16 }}
                    transition={{ duration: 0.3 }}
                    className="max-w-2xl mx-auto bg-white border border-stone-200 rounded-3xl overflow-hidden shadow-lg shadow-stone-100"
                    data-testid="order-summary"
                  >
                    <div className="bg-gradient-to-r from-[#1A4331] to-[#2A5A44] px-7 py-4">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Your Selection</p>
                    </div>

                    <div className="divide-y divide-stone-100">
                      {activeSubs.map(type => {
                        const sub = selectedSubs[type];
                        const meta = SUB_META[type];
                        const price = getSubPrice(type);
                        let label = meta.title;
                        if (type === 'post-workout' && sub.plan) {
                          label = sub.plan === 'weight-loss' ? 'Post-Workout · Weight Loss' : 'Post-Workout · Muscle Gain';
                        } else if (type === 'post-workout') {
                          label = 'Post-Workout · Goal not selected';
                        }
                        return (
                          <div key={type} className="flex items-center justify-between px-7 py-4" data-testid={`summary-item-${type}`}>
                            <div>
                              <p className="text-sm font-semibold text-[#1C1917]">{label}</p>
                              <p className="text-xs text-stone-500 capitalize">{sub.billing}</p>
                            </div>
                            <p className="text-base font-bold text-[#1A4331]">
                              {type === 'post-workout' && !sub.plan
                                ? <span className="text-amber-600 text-sm">Select goal ↑</span>
                                : `₹${price.toLocaleString()}`
                              }
                            </p>
                          </div>
                        );
                      })}
                    </div>

                    <div className="px-7 py-5 bg-stone-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-stone-500">Total</p>
                        <p className="text-2xl font-bold text-[#1A4331]" data-testid="order-total">
                          ₹{orderTotal.toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={handleStep1Continue}
                        className="bg-[#1A4331] text-white px-8 py-3.5 rounded-full font-medium hover:bg-[#2A5A44] transition-all flex items-center gap-2 shadow-lg shadow-[#1A4331]/20 hover:shadow-xl hover:-translate-y-0.5"
                        data-testid="step1-continue-btn"
                      >
                        Continue to Meal Selection
                        <ArrowRight size={18} weight="bold" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {activeSubs.length === 0 && (
                <div className="text-center mt-4">
                  <p className="text-sm text-stone-400">Select one or more plans above to see your order summary</p>
                </div>
              )}

              {/* Custom Order Callout */}
              <div className="mt-16 max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5 }}
                  className="relative bg-gradient-to-br from-[#0F2A20] via-[#1A4331] to-[#2A5A44] text-white rounded-[2rem] p-8 md:p-10 overflow-hidden shadow-2xl shadow-[#1A4331]/20"
                >
                  <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 bg-[#34D399]/20 rounded-full blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-20 -left-16 w-56 h-56 bg-[#C05C3D]/15 rounded-full blur-3xl" />
                  <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '18px 18px' }} />
                  <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
                    <div className="flex-1">
                      <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 text-[#34D399] px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] mb-4">
                        <Sparkle size={12} weight="fill" /> Off-Menu
                      </div>
                      <h3 className="text-2xl md:text-3xl font-medium font-heading tracking-tight mb-2">Not seeing what you crave?</h3>
                      <p className="text-stone-200 text-sm md:text-base leading-relaxed max-w-lg">
                        Request a custom meal — biryani, a specialty diet dish, or anything else. Our chefs will prepare it fresh, delivered to your door.
                      </p>
                    </div>
                    <button
                      onClick={() => setCustomOrderOpen(true)}
                      className="bg-[#34D399] text-[#1A4331] px-6 py-3.5 rounded-full font-bold hover:bg-white transition-all shadow-xl shadow-black/20 flex items-center gap-2 whitespace-nowrap"
                      data-testid="plans-custom-order-btn"
                    >
                      <ForkKnife size={18} weight="duotone" /> Custom Order
                    </button>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* ── STEP 2: Meal selection ──────────────────────────────────── */}
          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} data-testid="step-2">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-stone-100 px-4 py-2 rounded-full text-sm font-medium text-stone-700 mb-4">
                  <Plant size={16} weight="duotone" className="text-[#1A4331]" />
                  Your Weekly Menu
                </div>
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4">
                  Customise Your Meals
                </h1>
                <p className="text-base leading-relaxed text-stone-600 max-w-2xl mx-auto">
                  Review your weekly menu for each subscription. Select your preferences where available.
                </p>
              </div>

              <div className="space-y-14">
                {activeSubs.map(type => {
                  const planData = getPlanDataForSub(type);
                  if (!planData) return null;
                  const meta = SUB_META[type];
                  const sub = selectedSubs[type];
                  let sectionTitle = planData.title;
                  if (type === 'post-workout') {
                    sectionTitle = sub.plan === 'weight-loss' ? 'Post-Workout · Weight Loss Plan' : 'Post-Workout · Muscle Gain Plan';
                  }

                  return (
                    <div key={type} data-testid={`meal-section-${type}`}>
                      {/* Section header */}
                      <div className="flex items-center gap-3 mb-7">
                        <div className="w-1.5 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.18em] mb-0.5" style={{ color: meta.color }}>{meta.tag}</p>
                          <h2 className="text-xl sm:text-2xl font-semibold text-[#1C1917] font-heading">{sectionTitle}</h2>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {planData.meals.map((meal, idx) => (
                          <motion.div
                            key={`${type}-${meal.day}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * idx }}
                            className="bg-white border border-stone-100 shadow-sm hover:shadow-lg transition-all duration-300 rounded-3xl overflow-hidden"
                            data-testid={`meal-card-${type}-${meal.day.toLowerCase()}`}
                          >
                            <div className="aspect-square overflow-hidden bg-stone-50">
                              <img src={meal.image} alt={meal.name} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="p-5">
                              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-1.5" style={{ color: meta.color }}>{meal.day}</p>
                              <h3 className="text-base font-semibold text-[#1C1917] mb-2 font-heading leading-snug">{meal.name}</h3>
                              {meal.description && (
                                <p className="text-xs text-stone-500 mb-3">{meal.description}</p>
                              )}
                              {meal.calories && (
                                <div className="grid grid-cols-3 gap-2 mb-4">
                                  <div className="bg-stone-50 rounded-lg p-2 text-center">
                                    <p className="text-xs text-stone-500">Cal</p>
                                    <p className="text-sm font-bold text-[#1C1917]">{meal.calories}</p>
                                  </div>
                                  <div className="bg-stone-50 rounded-lg p-2 text-center">
                                    <p className="text-xs text-stone-500">Protein</p>
                                    <p className="text-sm font-bold text-[#1C1917]">{meal.protein}g</p>
                                  </div>
                                  <div className="bg-stone-50 rounded-lg p-2 text-center">
                                    <p className="text-xs text-stone-500">Fibre</p>
                                    <p className="text-sm font-bold text-[#1C1917]">{meal.fibre}g</p>
                                  </div>
                                </div>
                              )}
                              {meal.options && (
                                <div className="space-y-2 pt-3 border-t border-stone-100">
                                  <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Choose one:</p>
                                  {meal.options.map(opt => {
                                    const isSelected = mealChoices[type][meal.day] === opt;
                                    const isFlashing = flashedOption?.subType === type && flashedOption?.day === meal.day && flashedOption?.opt === opt;
                                    return (
                                      <label
                                        key={opt}
                                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                                          isSelected ? 'bg-stone-100 border border-stone-300' : 'hover:bg-stone-50'
                                        }`}
                                      >
                                        <input
                                          type="radio"
                                          name={`meal-${type}-${meal.day}`}
                                          value={opt}
                                          checked={isSelected}
                                          onChange={() => handleMealChoice(type, meal.day, opt)}
                                          className="w-4 h-4 flex-shrink-0"
                                          style={{ accentColor: meta.color }}
                                          data-testid={`option-${type}-${meal.day.toLowerCase()}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                                        />
                                        <span className="text-sm text-stone-700 flex-1">{opt}</span>
                                        <AnimatePresence>
                                          {isFlashing && (
                                            <motion.span
                                              key="tick"
                                              initial={{ scale: 0, opacity: 1 }}
                                              animate={{ scale: 1, opacity: 1 }}
                                              exit={{ scale: 1.3, opacity: 0 }}
                                              transition={{ duration: 0.35, ease: 'backOut' }}
                                              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                                              style={{ backgroundColor: meta.color }}
                                            >
                                              <CheckCircle size={13} weight="fill" className="text-white" />
                                            </motion.span>
                                          )}
                                        </AnimatePresence>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center mt-12">
                <button
                  onClick={handleStep2Continue}
                  className="bg-[#1A4331] text-white px-10 py-4 rounded-full font-medium hover:bg-[#2A5A44] transition-all duration-200 inline-flex items-center gap-2 text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  data-testid="continue-to-form-btn"
                >
                  Continue
                  <ArrowRight size={20} weight="bold" />
                </button>
                <p className="mt-6 text-sm text-stone-500">
                  Want something not on this menu?{' '}
                  <button
                    onClick={() => setCustomOrderOpen(true)}
                    className="text-[#1A4331] font-semibold underline underline-offset-4 decoration-stone-300 hover:decoration-[#1A4331] transition-colors"
                    data-testid="step2-custom-order-btn"
                  >
                    Request a Custom Order →
                  </button>
                </p>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: Customer form ───────────────────────────────────── */}
          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="max-w-2xl mx-auto" data-testid="step-3">
              <div className="text-center mb-10">
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4">Your Details</h1>
                <p className="text-base leading-relaxed text-stone-600">A few quick details and you&apos;re done.</p>
              </div>

              <form onSubmit={handleSubmit} className="bg-white border border-stone-100 shadow-sm rounded-3xl p-8 space-y-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all"
                    required
                    data-testid="start-date-input"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all"
                    required
                    data-testid="full-name-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Mobile Number *</label>
                    <input
                      type="tel"
                      value={formData.mobile}
                      onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                      className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all"
                      placeholder="+91 9876543210"
                      required
                      data-testid="mobile-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all"
                      placeholder="you@example.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Choose your Fitness Center *</label>
                  <select
                    value={formData.fitness_center_id}
                    onChange={e => {
                      const c = fitnessCenters.find(x => x.center_id === e.target.value);
                      const displayName = c ? `${c.name}${c.city ? ' — ' + c.city : ''}` : '';
                      setFormData({ ...formData, fitness_center_id: e.target.value, fitness_center_name: displayName });
                    }}
                    className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all appearance-none cursor-pointer"
                    required
                    data-testid="fitness-center-select"
                  >
                    <option value="">Select your nearest delivery center…</option>
                    {fitnessCenters.map(c => (
                      <option key={c.center_id} value={c.center_id}>
                        {c.name}{c.city ? ` — ${c.city}` : ''}{c.address && !c.city ? ` — ${c.address}` : ''}
                      </option>
                    ))}
                  </select>
                  {fitnessCenters.length === 0 && <p className="text-xs text-stone-500 mt-1.5">Loading delivery centers…</p>}
                  <p className="text-xs text-stone-400 mt-1.5">
                    Don&apos;t see your gym? Ask us to add it — your meals will be waiting at the front desk.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Address Line 1 *</label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={e => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all"
                    placeholder="House/Flat number, Street"
                    required
                    data-testid="address1-input"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Address Line 2</label>
                  <input
                    type="text"
                    value={formData.address_line2}
                    onChange={e => setFormData({ ...formData, address_line2: e.target.value })}
                    className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all"
                    placeholder="Landmark, Area (optional)"
                    data-testid="address2-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">City *</label>
                    <input type="text" value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="city-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">State *</label>
                    <input type="text" value={formData.state} onChange={e => setFormData({ ...formData, state: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="state-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Zipcode *</label>
                    <input type="text" value={formData.zipcode} onChange={e => setFormData({ ...formData, zipcode: e.target.value })} className="w-full bg-stone-50 border border-stone-200 focus:border-[#1A4331] focus:ring-1 focus:ring-[#1A4331] rounded-xl px-4 py-3 outline-none transition-all" required data-testid="zipcode-input" />
                  </div>
                </div>

                {/* Order recap */}
                <div className="bg-stone-50 rounded-2xl p-5 border border-stone-100">
                  <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">Order Summary</p>
                  <div className="space-y-2">
                    {activeSubs.map(type => {
                      const sub = selectedSubs[type];
                      const meta = SUB_META[type];
                      let name = meta.title;
                      if (type === 'post-workout') name = sub.plan === 'weight-loss' ? 'Post-Workout · Weight Loss' : 'Post-Workout · Muscle Gain';
                      return (
                        <div key={type} className="flex items-center justify-between">
                          <span className="text-sm text-stone-700">{name} <span className="text-stone-400 capitalize">({sub.billing})</span></span>
                          <span className="text-sm font-semibold text-[#1A4331]">₹{getSubPrice(type).toLocaleString()}</span>
                        </div>
                      );
                    })}
                    <div className="border-t border-stone-200 pt-2 flex items-center justify-between">
                      <span className="text-sm font-bold text-stone-800">Total</span>
                      <span className="text-base font-bold text-[#1A4331]">₹{orderTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#1A4331] text-white px-6 py-4 rounded-full font-medium hover:bg-[#2A5A44] transition-all duration-200 disabled:opacity-50"
                  data-testid="submit-request-btn"
                >
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </form>
            </motion.div>
          )}

          {/* ── STEP 4: Confirmation ────────────────────────────────────── */}
          {step === 4 && confirmation && (
            <motion.div key="step4" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="max-w-2xl mx-auto text-center py-12 relative" data-testid="step-4">
              <ConfettiBurst show count={48} />
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 180, damping: 14 }}
                className="w-28 h-28 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-[#10B981]/30 relative z-10"
              >
                <CheckCircle size={64} weight="fill" className="text-white" />
              </motion.div>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4" data-testid="confirmation-heading">Request Received!</h1>
              <p className="text-base leading-relaxed text-stone-600 mb-8 max-w-md mx-auto">Thank you for choosing Nova Foods. We&apos;ve sent a confirmation to your email.</p>

              <div className="bg-white border border-stone-100 rounded-3xl p-8 mb-8 max-w-md mx-auto shadow-xl shadow-stone-200/40">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Your Request ID</p>
                <p className="text-3xl font-bold text-[#1A4331] mb-4 font-heading tracking-tight" data-testid="request-id">{confirmation.request_id}</p>
                <div className="inline-flex items-center gap-2 bg-[#FBBF24]/20 text-[#92400E] px-4 py-2 rounded-full text-sm font-medium">
                  <Clock size={16} weight="duotone" />
                  Pending Approval
                </div>
              </div>

              <p className="text-stone-600 mb-6">Save your Request ID. We&apos;ll contact you within 24 hours.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => navigate('/login')} className="bg-[#1A4331] text-white px-8 py-3.5 rounded-full font-semibold hover:bg-[#2A5A44] transition-all shadow-lg shadow-[#1A4331]/20 hover:shadow-xl hover:-translate-y-0.5" data-testid="login-to-track-btn">
                  Login to Track Status
                </button>
                <button onClick={() => navigate('/')} className="border border-stone-300 bg-white text-stone-700 px-8 py-3.5 rounded-full font-semibold hover:bg-stone-50 transition-all" data-testid="back-home-btn">
                  Back to Home
                </button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </main>

      <CustomOrderModal open={customOrderOpen} onClose={() => setCustomOrderOpen(false)} />
    </div>
  );
};

export default PlansPage;
